import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../layout/Sidebar";
import Topbar from "../layout/Topbar";
import { exportJobPDF } from "../utils/exportJobPDF";
import "../styles/dashboard.css";
import API from "../services/api";

const STATUS_CLS = {
  ongoing:   "p-ongoing",
  pending:   "p-pending",
  completed: "p-completed",
  hold:      "p-hold",
  revisit:   "p-revisit",
  draft:     "p-draft",
};

function SortIcon({ col, sortKey, sortDir }) {
  if (sortKey !== col) return <span className="sort-icon sort-off">⇅</span>;
  return <span className="sort-icon sort-on">{sortDir === "asc" ? "↑" : "↓"}</span>;
}

function DataTable({ columns, rows, sortKey, sortDir, onSort, loading, emptyMsg }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.sortable ? "th-sort" : ""} onClick={() => c.sortable && onSort(c.key)}>
                {c.label}
                {c.sortable && <SortIcon col={c.key} sortKey={sortKey} sortDir={sortDir} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length} className="td-loading"><span className="td-spinner" /> Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="td-empty">{emptyMsg}</td></tr>
          ) : (
            rows.map((row, i) => (
              <tr key={row.id ?? i}>
                {columns.map((c) => (
                  <td key={c.key}>{c.render ? c.render(row) : row[c.key]}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ meta, onPageChange, onPerPageChange, perPage }) {
  if (!meta) return null;
  const { current_page, last_page, from, to, total } = meta;
  return (
    <div className="dt-pagination">
      <div className="dt-rpp">
        <span>Rows per page:</span>
        <select value={perPage} onChange={(e) => onPerPageChange(Number(e.target.value))}>
          {[5, 10, 25].map((n) => <option key={n}>{n}</option>)}
        </select>
      </div>
      <span className="dt-info">{total === 0 ? "0 results" : `${from}–${to} of ${total}`}</span>
      <div className="dt-nav">
        <button className="pg-btn" onClick={() => onPageChange(1)}                disabled={current_page === 1}>«</button>
        <button className="pg-btn" onClick={() => onPageChange(current_page - 1)} disabled={current_page === 1}>‹</button>
        <button className="pg-btn" onClick={() => onPageChange(current_page + 1)} disabled={current_page === last_page}>›</button>
        <button className="pg-btn" onClick={() => onPageChange(last_page)}        disabled={current_page === last_page}>»</button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.role?.toLowerCase()?.replace("_", " ");

  const [showSidebar, setShowSidebar] = useState(false);
  const [printLoading, setPrintLoading] = useState({});

  const [notif,        setNotif]        = useState(null);
  const [counts,       setCounts]       = useState({ ongoing: 0, completed_today: 0, not_completed: 0, total_jobs: 0 });
  const [notifLoading, setNotifLoading] = useState(true);

  const [openJobs,    setOpenJobs]    = useState([]);
  const [openMeta,    setOpenMeta]    = useState(null);
  const [openPage,    setOpenPage]    = useState(1);
  const [openPer,     setOpenPer]     = useState(10);
  const [openSearch,  setOpenSearch]  = useState("");
  const [openQuery,   setOpenQuery]   = useState("");
  const [openSortKey, setOpenSortKey] = useState(null);
  const [openSortDir, setOpenSortDir] = useState("asc");
  const [openLoading, setOpenLoading] = useState(true);

  const [compJobs,    setCompJobs]    = useState([]);
  const [compMeta,    setCompMeta]    = useState(null);
  const [compPage,    setCompPage]    = useState(1);
  const [compPer,     setCompPer]     = useState(10);
  const [compSearch,  setCompSearch]  = useState("");
  const [compQuery,   setCompQuery]   = useState("");
  const [compSortKey, setCompSortKey] = useState(null);
  const [compSortDir, setCompSortDir] = useState("asc");
  const [compLoading, setCompLoading] = useState(true);

  /* Notifications */
  useEffect(() => {
    setNotifLoading(true);
    API.get("/job-notifications")
      .then((res) => {
        const d = res.data;
        if (d.success) {
          setNotif(d.notification);
          setCounts(d.counts || { ongoing: 0, completed_today: 0, not_completed: 0, total_jobs: 0 });
        }
      })
      .catch((err) => console.error("Notification API error:", err))
      .finally(() => setNotifLoading(false));
  }, []);

  /* Open jobs */
  useEffect(() => {
    setOpenLoading(true);
    const params = { page: openPage, per_page: openPer, status: "open" };
    if (openQuery) params.search = openQuery;

    API.get("/jobs-reports", { params })
      .then((res) => {
        const d = res.data;
        if (d.success) {
          let rows = d.data.data ?? [];
          if (openSortKey) {
            rows = [...rows].sort((a, b) => {
              const av = String(a[openSortKey] ?? "").toLowerCase();
              const bv = String(b[openSortKey] ?? "").toLowerCase();
              return openSortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
            });
          }
          setOpenJobs(rows);
          setOpenMeta(d.data);
        }
      })
      .catch((err) => console.error("Open Jobs API error:", err))
      .finally(() => setOpenLoading(false));
  }, [openPage, openPer, openQuery, openSortKey, openSortDir]);

  /* Completed today */
  useEffect(() => {
    setCompLoading(true);
    const params = { page: compPage, per_page: compPer };
    if (compQuery) params.search = compQuery;

    API.get("/jobs/report/completed-today", { params })
      .then((res) => {
        const d = res.data;
        if (d.success) {
          let rows = d.data.data ?? [];
          if (compSortKey) {
            rows = [...rows].sort((a, b) => {
              const av = String(a[compSortKey] ?? "").toLowerCase();
              const bv = String(b[compSortKey] ?? "").toLowerCase();
              return compSortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
            });
          }
          setCompJobs(rows);
          setCompMeta(d.data);
        }
      })
      .catch((err) => console.error("Completed Jobs API error:", err))
      .finally(() => setCompLoading(false));
  }, [compPage, compPer, compQuery, compSortKey, compSortDir]);

  /* Print handler */
  const handlePrint = async (jobId) => {
    setPrintLoading((p) => ({ ...p, [jobId]: true }));
    try {
      await exportJobPDF(jobId, "print");
    } catch (err) {
      console.error("Print failed:", err);
      alert(err?.message ? `Failed to open print preview: ${err.message}` : "Failed to open print preview. Please try again.");
    } finally {
      setPrintLoading((p) => ({ ...p, [jobId]: false }));
    }
  };

  const handleOpenSort = (key) => {
    if (openSortKey === key) setOpenSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setOpenSortKey(key); setOpenSortDir("asc"); }
  };

  const handleCompSort = (key) => {
    if (compSortKey === key) setCompSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setCompSortKey(key); setCompSortDir("asc"); }
  };

  const handleViewJob = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  /* Action cell */
  const ActionCell = ({ row }) => (
    <div className="action-btns-row">
      <button
        className="btn btn-outline btn-xs"
        onClick={() => handleViewJob(row.id)}
        title="View Job Details"
      >
        View →
      </button>
      <button
        className="btn btn-outline btn-xs"
        onClick={() => handlePrint(row.id)}
        disabled={!!printLoading[row.id]}
        title="Print Job Report"
      >
        {printLoading[row.id] ? <span className="pdf-spinner" /> : "🖨️ Print"}
      </button>
    </div>
  );

  const openCols = [
    { key: "id",          label: "JOB ID",      sortable: true,  render: (r) => <span className="td-id" style={{ cursor: "pointer" }} onClick={() => handleViewJob(r.id)}>#{r.id}</span> },
    { key: "client_name", label: "CLIENT NAME",  sortable: true,  render: (r) => <span className="td-name">{r.client_name}</span> },
    { key: "location",    label: "LOCATION",     sortable: true  },
    { key: "staff_name",  label: "STAFF",        sortable: true,  render: (r) => (Array.isArray(r.staff) ? r.staff.map(s=>s.name||s).join(", ") : r.staff_name || "—") },
    { key: "status",      label: "STATUS",       sortable: true,
      render: (r) => <span className={`pill ${STATUS_CLS[r.status?.toLowerCase()] ?? ""}`}>{r.status}</span> },
    { key: "_actions",    label: "ACTIONS",      sortable: false, render: (r) => <ActionCell row={r} /> },
  ];

  const compCols = [
    { key: "id",          label: "JOB ID",      sortable: true,  render: (r) => <span className="td-id" style={{ cursor: "pointer" }} onClick={() => handleViewJob(r.id)}>#{r.id}</span> },
    { key: "client_name", label: "CLIENT NAME",  sortable: true,  render: (r) => <span className="td-name">{r.client_name}</span> },
    { key: "location",    label: "LOCATION",     sortable: true  },
    { key: "staff_name",  label: "STAFF",        sortable: true,  render: (r) => (Array.isArray(r.staff) ? r.staff.map(s=>s.name||s).join(", ") : r.staff_name || "—") },
    { key: "_actions",    label: "ACTIONS",      sortable: false, render: (r) => <ActionCell row={r} /> },
  ];

  return (
    <div className="app">
      {showSidebar && <div className="overlay show" onClick={() => setShowSidebar(false)} />}
      <Sidebar show={showSidebar} closeSidebar={() => setShowSidebar(false)} />

      <div className="main">
        <Topbar toggleSidebar={() => setShowSidebar((p) => !p)} />

        <div className="page">

          {!notifLoading &&
            notif?.text &&
            (counts.ongoing > 0 ||
              counts.completed_today > 0 ||
              counts.not_completed > 0 ||
              counts.total_jobs > 0) && (
              <div className="notif-banner">🔔 {notif.text}</div>
          )}

          <div className="stats-row">
            <div className="stat s-blue">
              <div className="sl">Ongoing Jobs</div>
              <div className="sv">{notifLoading ? "—" : counts.ongoing}</div>
              <div className="sm">Currently active</div>
            </div>
            <div className="stat s-green">
              <div className="sl">Completed Today</div>
              <div className="sv">{notifLoading ? "—" : counts.completed_today}</div>
              <div className="sm">Finished today</div>
            </div>
            <div className="stat s-amber">
              <div className="sl">Open Jobs</div>
              <div className="sv">{notifLoading ? "—" : counts.not_completed}</div>
              <div className="sm">Not completed records</div>
            </div>
            <div className="stat s-purple">
              <div className="sl">Total Jobs</div>
              <div className="sv">{notifLoading ? "—" : counts.total_jobs}</div>
              <div className="sm">All active records</div>
            </div>
          </div>

          {/* Open Jobs */}
          <div className="card">
            <div className="card-hd">
              <div>
                <h3>Open Jobs</h3>
                <div className="sub">Pending &amp; Ongoing</div>
              </div>
              <div className="card-hd-right">
                <div className="dt-search-wrap">
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" className="dt-search-icon">
                    <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6"/>
                    <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                  <input className="dt-search" type="text" placeholder="Search jobs…" value={openSearch}
                    onChange={(e) => setOpenSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { setOpenQuery(openSearch); setOpenPage(1); } }}
                  />
                  {openSearch && <button className="dt-search-clear" onClick={() => { setOpenSearch(""); setOpenQuery(""); setOpenPage(1); }}>✕</button>}
                </div>
                {(role === "admin" || role === "supervisor" || role === "team leader") && (
                  <button
                    className="btn btn-blue btn-sm"
                    onClick={() => navigate("/create-job")}
                  >
                    + Create Job
                  </button>
                )}
              </div>
            </div>
            <DataTable columns={openCols} rows={openJobs} sortKey={openSortKey} sortDir={openSortDir} onSort={handleOpenSort} loading={openLoading} emptyMsg="No open jobs found." />
            <Pagination meta={openMeta} perPage={openPer} onPageChange={(p) => setOpenPage(p)} onPerPageChange={(n) => { setOpenPer(n); setOpenPage(1); }} />
            <div className="card-ft">{openMeta ? `${openMeta.total ?? 0} open jobs — pending & ongoing` : "Loading…"}</div>
          </div>

          {/* Completed Today */}
          <div className="card">
            <div className="card-hd">
              <div>
                <h3>Completed Jobs</h3>
                <div className="sub">Today only</div>
              </div>
              <div className="card-hd-right">
                <div className="dt-search-wrap">
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" className="dt-search-icon">
                    <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6"/>
                    <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                  <input className="dt-search" type="text" placeholder="Search completed…" value={compSearch}
                    onChange={(e) => setCompSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { setCompQuery(compSearch); setCompPage(1); } }}
                  />
                  {compSearch && <button className="dt-search-clear" onClick={() => { setCompSearch(""); setCompQuery(""); setCompPage(1); }}>✕</button>}
                </div>
                <span className="pill p-completed">{counts.completed_today} today</span>
              </div>
            </div>
            <DataTable columns={compCols} rows={compJobs} sortKey={compSortKey} sortDir={compSortDir} onSort={handleCompSort} loading={compLoading} emptyMsg="No jobs completed today." />
            <Pagination meta={compMeta} perPage={compPer} onPageChange={(p) => setCompPage(p)} onPerPageChange={(n) => { setCompPer(n); setCompPage(1); }} />
            <div className="card-ft">{compMeta ? `${compMeta.total ?? 0} completed today` : "Loading…"}</div>
          </div>

        </div>
      </div>
    </div>
  );
}